/**
 * Apply FullStory CSS classes (fs-exclude, fs-mask) to elements with data-fs-* attributes.
 * This provides a programmatic backup in case FullStory doesn't pick up the data attributes.
 */
const applyFullStoryClasses = (): void => {
  // Apply fs-exclude class to all elements with data-fs-exclude attribute
  const excludeElements = document.querySelectorAll('[data-fs-exclude]');
  excludeElements.forEach((el) => {
    if (!el.classList.contains('fs-exclude')) {
      el.classList.add('fs-exclude');
    }
  });

  // Apply fs-mask class to all elements with data-fs-mask attribute
  const maskElements = document.querySelectorAll('[data-fs-mask]');
  maskElements.forEach((el) => {
    if (!el.classList.contains('fs-mask')) {
      el.classList.add('fs-mask');
    }
  });

};

/**
 * Initialize FullStory after React components have rendered
 * This ensures all data-fs-exclude attributes are in the DOM before FullStory scans it
 */
export const initFullStory = (): void => {
  // Check if already initialized to prevent multiple calls
  if ((window as any)._fs_initialized) {
    console.log('FullStory: Already initialized, skipping');
    return;
  }

  // Check if FullStory config is present (set by index.ejs)
  if (!(window as any)._fs_defer_init) {
    console.log('FullStory: Deferred init flag not set, skipping');
    return;
  }

  // Check environment - only run in staging
  if (!(window as any)._fs_org) {
    console.log('FullStory: Config not found, skipping initialization');
    return;
  }

  // Log element counts for debugging
  const excludeElements = document.querySelectorAll('[data-fs-exclude]');
  const maskElements = document.querySelectorAll('[data-fs-mask]');

  // Apply CSS classes as programmatic backup BEFORE FullStory initializes
  applyFullStoryClasses();

  // Debounce timer for MutationObserver to batch rapid DOM changes
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Set up MutationObserver to handle dynamically added elements
  const observer = new MutationObserver((mutations) => {
    let needsUpdate = false;
    mutations.forEach((mutation) => {
      // Handle new elements added to DOM
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            if (
              node.hasAttribute('data-fs-exclude') ||
              node.hasAttribute('data-fs-mask') ||
              node.querySelector('[data-fs-exclude], [data-fs-mask]')
            ) {
              needsUpdate = true;
            }
          }
        });
      }
      // Handle attributes being added/changed on existing elements
      if (mutation.type === 'attributes') {
        const target = mutation.target as Element;
        if (
          target.hasAttribute('data-fs-exclude') ||
          target.hasAttribute('data-fs-mask')
        ) {
          needsUpdate = true;
        }
      }
    });

    if (needsUpdate) {
      // Debounce to batch rapid mutations (e.g. React re-renders)
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        applyFullStoryClasses();
      }, 50);
    }
  });

  // Start observing the document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-fs-exclude', 'data-fs-mask'],
  });

  console.log('FullStory: MutationObserver started for dynamic content');

  // Mark as initialized before running the script
  (window as any)._fs_initialized = true;

  // Initialize FullStory
  try {
    (function(m: any, n: any, e: any, t: any, l: any) {
      let o: any, g: any, y: any;
      if (e in m) {
        if (m.console && m.console.log) {
          m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');
        }
        return;
      }
      g = m[e] = function(a: any, b: any, s: any) {
        g.q ? g.q.push([a, b, s]) : g._api(a, b, s);
      };
      g.q = [];
      o = n.createElement(t);
      o.async = 1;
      o.crossOrigin = 'anonymous';
      o.src = 'https://' + (window as any)._fs_script;
      y = n.getElementsByTagName(t)[0];
      y.parentNode.insertBefore(o, y);
      g.identify = function(i: any, v: any, s: any) {
        g(l, { uid: i }, s);
        if (v) g(l, v, s);
      };
      g.setUserVars = function(v: any, s: any) {
        g(l, v, s);
      };
      g.event = function(i: any, v: any, s: any) {
        g('event', { n: i, p: v }, s);
      };
      g.anonymize = function() {
        g.identify(!!0);
      };
      g.shutdown = function() {
        g('rec', !1);
      };
      g.restart = function() {
        g('rec', !0);
      };
      g.log = function(a: any, b: any) {
        g('log', [a, b]);
      };
      g.consent = function(a: any) {
        g('consent', !arguments.length || a);
      };
      g.identifyAccount = function(i: any, v: any) {
        o = 'account';
        v = v || {};
        v.acctId = i;
        g(o, v);
      };
      g.clearUserCookie = function() {};
      g.setVars = function(n: any, p: any) {
        g('setVars', [n, p]);
      };
      (window as any)._fs_initialized = true;
    })(
      window,
      document,
      (window as any)._fs_namespace,
      'script',
      'user'
    );
    console.log('FullStory: Initialized successfully');
  } catch (error) {
    console.error('FullStory: Initialization failed', error);
  }
};
